import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchEventReactions, toggleEventReaction } from '../../api/reviews';

export function ThumbUpIcon() {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M2 21h4V9H2v12zM22 10c0-1.1-.9-2-2-2h-6.3l1-4.6v-.3c0-.4-.2-.8-.4-1.1L13.2 1 6.6 7.6c-.4.4-.6.9-.6 1.4v10c0 1.1.9 2 2 2h9c.8 0 1.5-.5 1.8-1.2l3-7.1c.1-.2.2-.5.2-.7v-2z" />
        </svg>
    );
}

export function ThumbDownIcon() {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M22 3h-4v12h4V3zM2 14c0 1.1.9 2 2 2h6.3l-1 4.6v.3c0 .4.2.8.4 1.1l1.1 1 6.6-6.6c.4-.4.6-.9.6-1.4V5c0-1.1-.9-2-2-2H7c-.8 0-1.5.5-1.8 1.2l-3 7.1c-.1.2-.2.5-.2.7v2z" />
        </svg>
    );
}

function ShareIcon() {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M18 16.1c-.8 0-1.5.3-2 .8l-7.1-4.2c.1-.2.1-.5.1-.7s0-.5-.1-.7L16 7.2c.5.5 1.2.8 2 .8 1.7 0 3-1.3 3-3s-1.3-3-3-3-3 1.3-3 3c0 .2 0 .5.1.7L8 9.8C7.5 9.3 6.8 9 6 9c-1.7 0-3 1.3-3 3s1.3 3 3 3c.8 0 1.5-.3 2-.8l7.1 4.2c-.1.2-.1.4-.1.6 0 1.6 1.3 2.9 2.9 2.9s2.9-1.3 2.9-2.9-1.2-2.9-2.8-2.9z" />
        </svg>
    );
}

export default function ReactionBar({ eventPk, requireLogin }) {
    const queryClient = useQueryClient();

    const { data: reactions } = useQuery({
        queryKey: ['eventReactions', eventPk],
        queryFn: () => fetchEventReactions(eventPk),
    });

    const toggleMutation = useMutation({
        mutationFn: (reaction) => toggleEventReaction(eventPk, reaction),
        onSuccess: (data) =>
            queryClient.setQueryData(['eventReactions', eventPk], data),
        onError: () => alert('Failed to update reaction. Please try again.'),
    });

    const handleReaction = (reaction) => {
        if (!requireLogin('react')) return;
        toggleMutation.mutate(reaction);
    };

    const handleShare = async () => {
        const url = window.location.href;
        if (navigator.share) {
            try {
                await navigator.share({ url });
            } catch {
                // User cancelled the share sheet — nothing to do.
            }
            return;
        }
        try {
            await navigator.clipboard.writeText(url);
            alert('Link copied to clipboard!');
        } catch {
            alert('Failed to copy the link.');
        }
    };

    return (
        <div className="reaction-bar">
            <button
                className={`reaction-btn ${reactions?.my_reaction === 'like' ? 'active' : ''}`}
                aria-label="Like"
                onClick={() => handleReaction('like')}
                disabled={toggleMutation.isPending}
            >
                <ThumbUpIcon />
                {reactions?.likes ?? 0}
            </button>
            <button
                className={`reaction-btn ${reactions?.my_reaction === 'dislike' ? 'active' : ''}`}
                aria-label="Dislike"
                onClick={() => handleReaction('dislike')}
                disabled={toggleMutation.isPending}
            >
                <ThumbDownIcon />
            </button>
            <button className="reaction-btn" onClick={handleShare}>
                <ShareIcon />
                Share
            </button>
        </div>
    );
}
